/**
 * ─────────────────────────────────────────────────────────────────
 * Detalle de una lista de compra.
 *
 * Versión móvil:
 *  - Formulario añadir: layout vertical en móvil (antes 4 campos en fila)
 *  - Cantidad y unidad en la misma fila, producto ocupa todo el ancho
 *  - Header compacto: título h5 + botones icon-only en móvil
 *  - Artículos: área táctil mínima 44px, checkbox más grande
 *  - Botón eliminar con minHeight 44px
 *  - isMobile con useMediaQuery (reactivo)
 *  - Estado vacío con ilustración amigable
 * ─────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    TextField,
    Typography,
    useTheme,
    useMediaQuery,
    Tooltip,
    Chip,
    Paper,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import DeleteIcon        from "@mui/icons-material/Delete";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import PictureAsPdfIcon  from "@mui/icons-material/PictureAsPdf";
import AddIcon           from "@mui/icons-material/Add";
import ShoppingCartIcon  from "@mui/icons-material/ShoppingCart";
import IosShareIcon      from "@mui/icons-material/IosShare";
import PrintIcon         from "@mui/icons-material/Print";
import { useCompartir, formatearListaParaCompartir } from "../hooks/useCompartir";

import {
    obtenerArticulos,
    agregarArticulosBatch,
    marcarArticuloComprado,
    eliminarArticulo,
} from "../api/articulos";
import { obtenerLista } from "../api/listas";

export default function DetalleLista() {
    const { idLista } = useParams();
    const navigate    = useNavigate();
    const printRef    = useRef(null);
    const theme       = useTheme();

    /**
     * isMobile reactivo — se actualiza al rotar el dispositivo.
     */
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { compartir, compartiendo } = useCompartir();
    const [nombreLista, setNombreLista] = useState("");
    const [articulos,   setArticulos]   = useState([]);
    const [cargando,    setCargando]    = useState(true);

    const [nombre,    setNombre]    = useState("");
    const [cantidad,  setCantidad]  = useState(1);
    const [unidad,    setUnidad]    = useState("ud");
    const [guardando, setGuardando] = useState(false);

    /** Carga el nombre de la lista y sus artículos */
    async function cargarTodo() {
        setCargando(true);
        try {
            const lista = await obtenerLista(idLista);
            setNombreLista(lista.nombre);
            const arts = await obtenerArticulos(idLista);
            setArticulos(Array.isArray(arts) ? arts : []);
        } catch (err) {
            console.error("Error cargando detalle lista:", err);
        } finally {
            setCargando(false);
        }
    }

    useEffect(() => {
        cargarTodo();
        // eslint-disable-next-line
    }, [idLista]);

    /** Añade un artículo manual a la lista */
    async function handleAgregar() {
        if (!nombre.trim()) return;
        setGuardando(true);
        try {
            await agregarArticulosBatch(idLista, [
                { nombre: nombre.trim(), cantidad, unidad },
            ]);
            setNombre("");
            setCantidad(1);
            setUnidad("ud");
            await cargarTodo();
        } finally {
            setGuardando(false);
        }
    }

    /** Alterna el estado comprado/no comprado de un artículo */
    async function toggleComprado(a) {
        await marcarArticuloComprado(idLista, a.idarticulo, !a.comprado);
        setArticulos((prev) =>
            prev.map((x) =>
                x.idarticulo === a.idarticulo ? { ...x, comprado: !x.comprado } : x
            )
        );
    }

    /** Elimina un artículo de la lista */
    async function handleEliminar(a) {
        await eliminarArticulo(idLista, a.idarticulo);
        setArticulos((prev) => prev.filter((x) => x.idarticulo !== a.idarticulo));
    }

    const comparados = articulos.filter((a) => a.precio_unitario != null);
    const manuales   = articulos.filter((a) => a.precio_unitario == null);

    /** Total estimado sumando solo artículos con precio conocido */
    const totalEstimado = useMemo(
        () => comparados.reduce((sum, a) => sum + a.precio_unitario * (a.cantidad || 1), 0),
        [comparados]
    );

    return (
        <Box sx={{ maxWidth: 800, mx: "auto" }}>

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: isMobile ? 2 : 3 }}
            >
                <Typography
                    variant={isMobile ? "h5" : "h4"}
                    sx={{ fontWeight: 800, color: "text.primary", flex: 1, mr: 1 }}
                    noWrap
                >
                    {nombreLista || "Cargando..."}
                </Typography>

                <Stack direction="row" spacing={1}>
                    {/*
                     * En móvil: solo iconos para ahorrar espacio.
                     * En escritorio: botones con texto.
                     */}
                    <Tooltip title={isMobile ? "Compartir lista" : "Exportar PDF"} arrow>
                        {isMobile ? (
                            <IconButton
                                onClick={() => compartir({
                                    titulo: nombreLista,
                                    texto:  formatearListaParaCompartir(nombreLista, comparados, manuales, totalEstimado),
                                })}
                                disabled={compartiendo}
                                size="small"
                                sx={{
                                    minWidth: 44, minHeight: 44,
                                    bgcolor:  alpha(theme.palette.primary.main, 0.1),
                                    color:    "primary.main", borderRadius: 2,
                                }}
                            >
                                <IosShareIcon fontSize="small" />
                            </IconButton>
                        ) : (
                            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
                                Exportar PDF
                            </Button>
                        )}
                    </Tooltip>

                    {isMobile ? (
                        <Tooltip title="Comparar precios" arrow>
                            <IconButton
                                onClick={() => navigate(`/compare?lista=${idLista}`)}
                                size="small"
                                sx={{
                                    minWidth: 44, minHeight: 44,
                                    bgcolor: "primary.main",
                                    color: "white", borderRadius: 2,
                                    "&:active": { transform: "scale(0.92)" },
                                }}
                            >
                                <CompareArrowsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button
                            variant="outlined"
                            startIcon={<CompareArrowsIcon />}
                            onClick={() => navigate(`/compare?lista=${idLista}`)}
                        >
                            Comparar
                        </Button>
                    )}
                </Stack>
            </Stack>

            {/* ── FORMULARIO AÑADIR ARTÍCULO ───────────────────────────── */}
            <Card
                sx={{
                    mb:           2,
                    borderRadius: 3,
                    border:       "1px solid",
                    borderColor:  "divider",
                    boxShadow:    "0 2px 8px rgba(0,0,0,0.04)",
                }}
            >
                <CardContent sx={{ p: isMobile ? "12px !important" : "16px !important" }}>
                    {isMobile ? (
                        /*
                         * LAYOUT MÓVIL:
                         * Fila 1: campo Producto (ancho completo)
                         * Fila 2: Cantidad + Unidad + botón Añadir
                         *
                         * Antes era todo en una fila horizontal → campos cortados
                         */
                        <Stack spacing={1.5}>
                            <TextField
                                label="Producto"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAgregar()}
                                fullWidth
                                size="small"
                                autoComplete="off"
                            />
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="Cantidad"
                                    type="number"
                                    value={cantidad}
                                    onChange={(e) => setCantidad(Number(e.target.value))}
                                    size="small"
                                    sx={{ width: 90 }}
                                    inputProps={{ min: 1 }}
                                />
                                <TextField
                                    label="Unidad"
                                    value={unidad}
                                    onChange={(e) => setUnidad(e.target.value)}
                                    size="small"
                                    sx={{ width: 80 }}
                                    autoComplete="off"
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAgregar}
                                    disabled={guardando || !nombre.trim()}
                                    sx={{
                                        flex:          1,
                                        minHeight:     44,
                                        borderRadius:  2,
                                        fontWeight:    700,
                                        textTransform: "none",
                                    }}
                                >
                                    {guardando
                                        ? <CircularProgress size={18} color="inherit" />
                                        : <><AddIcon sx={{ fontSize: 18, mr: 0.5 }} />Añadir</>
                                    }
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        /* LAYOUT ESCRITORIO: fila horizontal original */
                        <Stack direction="row" spacing={1}>
                            <TextField
                                label="Producto"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAgregar()}
                                fullWidth
                            />
                            <TextField
                                label="Cantidad"
                                type="number"
                                value={cantidad}
                                onChange={(e) => setCantidad(Number(e.target.value))}
                                sx={{ width: 110 }}
                                inputProps={{ min: 1 }}
                            />
                            <TextField
                                label="Unidad"
                                value={unidad}
                                onChange={(e) => setUnidad(e.target.value)}
                                sx={{ width: 90 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAgregar}
                                disabled={guardando || !nombre.trim()}
                                sx={{ minWidth: 100, fontWeight: 700, textTransform: "none" }}
                            >
                                {guardando ? <CircularProgress size={20} color="inherit" /> : "Añadir"}
                            </Button>
                        </Stack>
                    )}
                </CardContent>
            </Card>

            {/* ── LISTA DE ARTÍCULOS ───────────────────────────────────── */}
            {cargando ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>

            ) : articulos.length === 0 ? (
                /* Estado vacío */
                <Paper
                    sx={{
                        p:           isMobile ? 4 : 6,
                        textAlign:   "center",
                        borderRadius: 3,
                        border:      "2px dashed",
                        borderColor: "divider",
                        bgcolor:     "rgba(0,0,0,0.01)",
                    }}
                >
                    <ShoppingCartIcon sx={{ fontSize: 48, color: "divider", mb: 1.5 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        La lista está vacía
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                        Añade productos manualmente o usa el comparador
                    </Typography>
                </Paper>

            ) : (
                <Card
                    ref={printRef}
                    className="print-area"
                    sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
                >
                    <CardContent sx={{ p: isMobile ? "12px !important" : "16px !important" }}>
                        <Stack spacing={0}>

                            {/* Artículos comparados (con precio) */}
                            {comparados.length > 0 && (
                                <>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, px: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                            Comparados
                                        </Typography>
                                        <Chip label={comparados.length} size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                                    </Stack>

                                    {comparados.map((a) => (
                                        <ArticuloRow
                                            key={a.idarticulo}
                                            articulo={a}
                                            isMobile={isMobile}
                                            theme={theme}
                                            onToggle={() => toggleComprado(a)}
                                            onEliminar={() => handleEliminar(a)}
                                        />
                                    ))}
                                    <Divider sx={{ my: 1.5 }} />
                                </>
                            )}

                            {/* Artículos manuales (sin precio) */}
                            {manuales.length > 0 && (
                                <>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, px: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                            Manuales
                                        </Typography>
                                        <Chip label={manuales.length} size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                                    </Stack>

                                    {manuales.map((a) => (
                                        <ArticuloRow
                                            key={a.idarticulo}
                                            articulo={a}
                                            isMobile={isMobile}
                                            theme={theme}
                                            onToggle={() => toggleComprado(a)}
                                            onEliminar={() => handleEliminar(a)}
                                        />
                                    ))}
                                </>
                            )}

                            {/* Total estimado */}
                            {comparados.length > 0 && (
                                <Box
                                    sx={{
                                        mt:           2,
                                        pt:           1.5,
                                        borderTop:    "1px solid",
                                        borderColor:  "divider",
                                        display:      "flex",
                                        justifyContent: "flex-end",
                                        alignItems:   "center",
                                        gap:          1,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                        Total estimado
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main" }}>
                                        {totalEstimado.toFixed(2)} €
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}

/* ─────────────────────────────────────────────────────────────────
 * ArticuloRow
 * Fila de un artículo individual dentro de la lista.
 *
 * En móvil:
 *  - Checkbox más grande (táctil)
 *  - Botón eliminar con minHeight 44px
 *  - Info secundaria (supermercado/precio) en línea separada
 * ───────────────────────────────────────────────────────────────── */
function ArticuloRow({ articulo: a, isMobile, theme, onToggle, onEliminar }) {
    return (
        <Box
            sx={{
                py:           isMobile ? 0.5 : 0.75,
                "&:last-child .divider-row": { display: "none" },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">

                {/* Checkbox + texto */}
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Checkbox
                        checked={a.comprado}
                        onChange={onToggle}
                        sx={{
                            // Área táctil mínima 44px
                            padding: isMobile ? "10px" : "8px",
                            color:   "primary.main",
                        }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant={isMobile ? "body2" : "body1"}
                            sx={{
                                fontWeight:     600,
                                textDecoration: a.comprado ? "line-through" : "none",
                                opacity:        a.comprado ? 0.5 : 1,
                                overflow:       "hidden",
                                textOverflow:   "ellipsis",
                                whiteSpace:     "nowrap",
                            }}
                        >
                            {a.nombre}
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{ ml: 1, opacity: 0.6, fontWeight: 400 }}
                            >
                                {a.cantidad} {a.unidad}
                            </Typography>
                        </Typography>

                        {/* Info de precio/supermercado para artículos comparados */}
                        {a.precio_unitario != null && (
                            <Typography variant="caption" sx={{ opacity: 0.55, display: "block" }}>
                                {a.supermercado} · {a.precio_unitario.toFixed(2)} €/ud
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {/* Botón eliminar */}
                <IconButton
                    onClick={onEliminar}
                    size="small"
                    aria-label="Eliminar artículo"
                    sx={{
                        minWidth:  44,
                        minHeight: 44,
                        color:     "error.main",
                        bgcolor:   alpha(theme.palette.error.main, 0.08),
                        borderRadius: 2,
                        flexShrink: 0,
                        "&:hover":  { bgcolor: "error.main", color: "white" },
                        "&:active": { transform: "scale(0.92)" },
                    }}
                >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Stack>

            <Divider className="divider-row" sx={{ mt: 0.5 }} />
        </Box>
    );
}